import { useEffect, useRef, useState } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Message } from 'primereact/message'

const pluralize = (count, singular) => `${count} ${singular}${count === 1 ? '' : 's'}`

// Confirm step shown after a layout export file parses and before anything is created. Shared by the
// portal Layouts list and the public planner, so it imports nothing from the portal (auth, Apollo,
// Redux). `layout` is the parsed `{ name, integrations }`; `initialName` is the prefilled name field;
// onConfirm receives the trimmed name. While `busy` (the create is running) the Import button shows
// loading and the name field and Cancel are disabled; `error` is a failed attempt's message.
function ImportLayoutForm ({ layout, initialName, busy, error, onConfirm, onCancel }) {
  const [name, setName] = useState(initialName)
  const nameRef = useRef(null)
  // The form only mounts while the dialog is open, so this focuses the name field on every open.
  useEffect(() => {
    // Deferred so it lands after the dialog's own open-time focus handling.
    const timer = window.setTimeout(() => nameRef.current?.focus(), 0)
    return () => window.clearTimeout(timer)
  }, [])
  const integrations = layout.integrations
  const flowCount = integrations.reduce((count, integration) => count + integration.flows.length, 0)
  const trimmedName = name.trim()
  const submit = event => {
    event.preventDefault()
    if (trimmedName && !busy) onConfirm(trimmedName)
  }

  return (
    <form onSubmit={submit} className='layoutBuilder-import-dialog'>
      <label htmlFor='import-layout-name'>Layout name</label>
      <input
        id='import-layout-name'
        className='p-inputtext'
        value={name}
        ref={nameRef}
        disabled={busy}
        onChange={event => setName(event.target.value)}
      />
      <p className='layoutBuilder-import-summary'>
        {pluralize(integrations.length, 'integration')} · {pluralize(flowCount, 'planned flow')}
      </p>
      <ul className='layoutBuilder-import-integrations'>
        {integrations.map(integration => (
          <li key={integration.integration_uuid} aria-label={integration.name}>
            <strong>{integration.name}</strong>
            {integration.integration_type_label && <span> ({integration.integration_type_label})</span>}
            {integration.flows.length > 0 && (
              <ul>
                {integration.flows.map(flow => <li key={flow.flow_uuid}>{flow.name}</li>)}
              </ul>
            )}
          </li>
        ))}
      </ul>
      {error && <Message severity='error' text={error} />}
      <div className='layoutBuilder-import-actions'>
        <Button type='button' label='Cancel' outlined severity='secondary' disabled={busy} onClick={onCancel} />
        <Button type='submit' label={busy ? 'Importing…' : 'Import'} loading={busy} disabled={busy || !trimmedName} />
      </div>
    </form>
  )
}

export default function ImportLayoutDialog ({ layout, initialName, busy = false, error = null, onConfirm, onCancel }) {
  return (
    <Dialog
      header='Import layout'
      visible={Boolean(layout)}
      modal
      focusOnShow={false}
      style={{ width: 'min(36rem, calc(100vw - 2rem))' }}
      closable={!busy}
      onHide={() => { if (!busy) onCancel() }}
    >
      {layout && <ImportLayoutForm layout={layout} initialName={initialName} busy={busy} error={error} onConfirm={onConfirm} onCancel={onCancel} />}
    </Dialog>
  )
}
